/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Config-driven detail page: header with actions, field sections, optional
 * in-place editing, audit block, and related-record lists.
 *
 * Config keys: title, detailApiPath, primaryField, getTitle(record), route
 * (where to go after delete), supportsDelete, detailFields | detailSections
 * [{title, fields}], statusVariants, editFields (form specs; enables Edit),
 * updateMethod ("put" default), relatedRecords (see RelatedRecordsPanel),
 * headerActions(record, ctx) -> node, showAudit (default true).
 *
 * Field display spec: { label, field, renderValue(value, data), custom(data),
 * fullWidth, html, type: "status" }. A field named `status` renders as a chip.
 */

import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Card, Grid, IconButton, Skeleton, Snackbar, Tooltip } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import StatusBadge from "components/StatusBadge";
import { useAuth } from "auth/Auth";
import { usePermission } from "auth/usePermission";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { TravelDetailSection as DetailSection, TravelDetailFieldGrid as DetailFieldGrid } from "components/travel/TravelDetailSection";
import { commonIconButtonStyles, commonIconStyles } from "assets/theme/components/iconButtonStyles";
import RelatedRecordsPanel from "./RelatedRecordsPanel";
import EntityStatusChip from "./EntityStatusChip";
import EntityFormDialog from "./EntityFormDialog";
import EntityFormFields, { buildPayload, initialFormValues, missingRequired } from "./EntityFormFields";
import { DATE_PRIMARY_FIELDS, ENTITY_AUDIT_FIELDS, formatDate } from "./formatters";
import { apiErrorMessage } from "./listResponse";

const EntityDetail = ({ config, permissionObject, recordId }) => {
  const params = useParams();
  const id = recordId ?? params.id;
  const navigate = useNavigate();
  const location = useLocation();
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  // Dynamic like EntityList: the object comes from the route's config.
  const { canCreate, canEdit, canDelete } = usePermission(permissionObject);

  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [addSection, setAddSection] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback((message, severity = "success") => setSnackbar({ open: true, message, severity }), []);
  const nameFromState = location.state?.name;
  const relatedSections = config?.relatedRecords || [];
  const hasRelated = relatedSections.length > 0;

  const fetchRecord = useCallback(async () => {
    if (!config?.detailApiPath || !id || !axiosInstance || !isAuthReady || !isAuthenticated) return;
    try {
      const response = await axiosInstance.get(`${config.detailApiPath}/${id}`);
      setRecord(response.data);
    } catch (error) {
      console.error(`Error fetching ${config.title} detail:`, error);
      notify(`Error loading ${config.title} details`, "error");
    } finally {
      setIsLoading(false);
    }
  }, [config, id, axiosInstance, isAuthReady, isAuthenticated, notify]);

  useEffect(() => {
    setIsLoading(true);
    setRecord(null);
    setEditing(false);
  }, [id, config]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const headerTitle = useMemo(() => {
    if (!config) return "Detail";
    if (record && typeof config.getTitle === "function") {
      const t = config.getTitle(record);
      if (t) return t;
    }
    const primary = record?.[config.primaryField];
    if (primary != null && primary !== "") {
      return DATE_PRIMARY_FIELDS.has(config.primaryField) ? formatDate(primary) : String(primary);
    }
    if (nameFromState) {
      return DATE_PRIMARY_FIELDS.has(config.primaryField)
        ? formatDate(nameFromState) || nameFromState
        : nameFromState;
    }
    return config.title;
  }, [config, record, nameFromState]);

  const auditFields = useMemo(
    () =>
      ENTITY_AUDIT_FIELDS.map((field) => {
        if (!field.userIdField) return field;
        return {
          ...field,
          custom: (data) => {
            const name = data?.[field.field];
            const userId = data?.[field.userIdField];
            const canLink = Boolean(userId && name);
            return (
              <MDTypography variant="button" fontWeight="regular" color="text">
                <strong>{field.label}:</strong>{" "}
                {canLink ? (
                  <MDTypography
                    component="span"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/users/${userId}/profile`)}
                  >
                    {name}
                  </MDTypography>
                ) : (
                  name || "-"
                )}
              </MDTypography>
            );
          },
        };
      }),
    [navigate]
  );

  const decorateFields = useCallback(
    (fields) =>
      (fields || []).map((field) => {
        if (field.custom) return field;
        if (field.field === "status" || field.type === "status") {
          return {
            ...field,
            custom: (data) => (
              <MDTypography variant="button" fontWeight="regular" color="text">
                <strong>{field.label}:</strong>{" "}
                {config?.statusVariants || field.statusVariants ? (
                  <EntityStatusChip
                    status={data?.[field.field]}
                    variants={field.statusVariants || config.statusVariants}
                  />
                ) : (
                  <StatusBadge status={data?.[field.field] || "draft"} />
                )}
              </MDTypography>
            ),
          };
        }
        if (field.linkTo) {
          return {
            ...field,
            custom: (data) => {
              const path = field.linkTo(data);
              const value = field.renderValue
                ? field.renderValue(data?.[field.field], data)
                : data?.[field.field] ?? "-";
              return (
                <MDTypography variant="button" fontWeight="regular" color="text">
                  <strong>{field.label}:</strong>{" "}
                  <MDTypography
                    component="span"
                    variant="button"
                    fontWeight={path ? "medium" : "regular"}
                    color={path ? "info" : "text"}
                    sx={{ cursor: path ? "pointer" : "default" }}
                    onClick={() => path && navigate(path)}
                  >
                    {value === null || value === "" ? "-" : value}
                  </MDTypography>
                </MDTypography>
              );
            },
          };
        }
        if (field.renderValue) {
          // renderValue(value, data): bind the record so money fields see the currency.
          return { ...field, renderValue: (value) => field.renderValue(value, record) };
        }
        return field;
      }),
    [config, navigate, record]
  );

  const sections = useMemo(() => {
    if (!config) return [];
    if (Array.isArray(config.detailSections)) {
      return config.detailSections.map((s) => ({ ...s, fields: decorateFields(s.fields) }));
    }
    return [{ title: "Overview", fields: decorateFields(config.detailFields) }];
  }, [config, decorateFields]);

  const editable = Boolean(config?.editFields?.length) && canEdit;
  const allowDelete = Boolean(config?.supportsDelete && (permissionObject ? canDelete : true));

  const startEdit = () => {
    setFormValues(initialFormValues(config.editFields, record));
    setEditing(true);
  };

  const saveEdit = async () => {
    const missing = missingRequired(config.editFields, formValues);
    if (missing.length) {
      notify(`Required: ${missing.join(", ")}`, "error");
      return;
    }
    const payload = buildPayload(config.editFields, formValues, { onlyChangedFrom: record });
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const method = config.updateMethod || "put";
      await axiosInstance[method](`${config.detailApiPath}/${id}`, payload);
      notify(`${config.title} updated`);
      setEditing(false);
      await fetchRecord();
    } catch (error) {
      console.error(`Error updating ${config.title}:`, error);
      notify(apiErrorMessage(error, `Failed to update ${config.title.toLowerCase()}`), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`${config.deleteApiPath || config.detailApiPath}/${id}`);
      notify(`${config.title} deleted successfully`);
      setTimeout(() => navigate(config.route), 800);
    } catch (error) {
      console.error(`Error deleting ${config.title}:`, error);
      notify(apiErrorMessage(error, `Failed to delete ${config.title.toLowerCase()}`), "error");
      throw error;
    }
  };

  const ctx = { navigate, axiosInstance, refresh: fetchRecord, notify, canEdit, canCreate, canDelete, id };

  if (!config) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <MDTypography variant="h6">Unknown record type.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={hasRelated ? 8 : 12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox>
                  <MDTypography variant="h5" color="white">
                    {headerTitle}
                  </MDTypography>
                  <MDTypography variant="button" color="white" opacity={0.8}>
                    {config.title} details
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  {record && config.headerActions ? config.headerActions(record, ctx) : null}
                  {editable && !editing ? (
                    <Tooltip title="Edit" arrow>
                      <IconButton onClick={startEdit} sx={commonIconButtonStyles}>
                        <Edit sx={commonIconStyles} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {allowDelete && (
                    <Tooltip title="Delete" arrow>
                      <IconButton onClick={() => setDeleteOpen(true)} sx={commonIconButtonStyles}>
                        <Delete sx={commonIconStyles} />
                      </IconButton>
                    </Tooltip>
                  )}
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                {editing ? (
                  <DetailSection title={`Edit ${config.title}`}>
                    <EntityFormFields
                      fields={config.editFields}
                      values={formValues}
                      onChange={(name, value) => setFormValues((prev) => ({ ...prev, [name]: value }))}
                      disabled={saving}
                    />
                    <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" size="small" disabled={saving} onClick={() => setEditing(false)}>
                        Cancel
                      </MDButton>
                      <MDButton variant="gradient" color="info" size="small" disabled={saving} onClick={saveEdit}>
                        {saving ? "Saving..." : "Save"}
                      </MDButton>
                    </MDBox>
                  </DetailSection>
                ) : (
                  sections.map((section) => (
                    <DetailSection key={section.title} title={section.title}>
                      <DetailFieldGrid data={record} fields={section.fields} />
                    </DetailSection>
                  ))
                )}
                {config.renderExtra ? config.renderExtra(record, ctx) : null}
                {config.showAudit !== false ? (
                  <DetailSection title="Audit Information">
                    <DetailFieldGrid data={record} fields={auditFields} />
                  </DetailSection>
                ) : null}
              </MDBox>
            </Card>
          </Grid>

          {hasRelated ? (
            <Grid item xs={12} md={4}>
              <RelatedRecordsPanel
                sections={relatedSections}
                record={record}
                navigate={navigate}
                onAdd={(section) => setAddSection(section)}
                canAdd={config.relatedAddRequiresCreate ? canCreate : true}
              />
            </Grid>
          ) : null}
        </Grid>
      </MDBox>

      {addSection?.addForm ? (
        <EntityFormDialog
          open={Boolean(addSection)}
          onClose={() => setAddSection(null)}
          title={addSection.addForm.title || `Add ${addSection.title}`}
          apiPath={addSection.addForm.apiPath}
          fields={addSection.addForm.fields}
          defaults={addSection.addForm.defaults ? addSection.addForm.defaults(record) : null}
          onSubmit={
            addSection.addForm.onSubmit
              ? (payload) => addSection.addForm.onSubmit(payload, record, axiosInstance)
              : null
          }
          onCreated={() => {
            notify(`${addSection.title} added`);
            fetchRecord();
          }}
        />
      ) : null}

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title={`Delete ${config.title}`}
        message={`Are you sure you want to delete this ${config.title.toLowerCase()}? This action cannot be undone.`}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

EntityDetail.propTypes = {
  config: PropTypes.object,
  permissionObject: PropTypes.string,
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EntityDetail.defaultProps = {
  config: null,
  permissionObject: undefined,
  recordId: undefined,
};

export default EntityDetail;
